import { useState, useMemo } from 'react';
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight, RotateCcw } from 'lucide-react';
import type { FilterFieldDef, FilterPreset } from '../schema';
import type { ActiveFilter } from '../parser';
import { useI18n } from '@/context/I18nContext';

interface DateRangeFilterEditorProps {
  fieldDef: FilterFieldDef;
  filter?: ActiveFilter;
  onApply: (fromVal: string, toVal?: string) => void;
  onClose: () => void;
}

const DEFAULT_DATE_PRESETS: FilterPreset[] = [
  { label: 'filter.preset.today', value: 'today' },
  { label: 'filter.preset.last7d', value: '-7d' },
  { label: 'filter.preset.last30d', value: '-30d' },
  { label: 'filter.preset.last90d', value: '-90d' },
  { label: 'filter.preset.last1y', value: '-1y' },
];

function formatDateISO(year: number, month: number, day: number): string {
  const m = String(month + 1).padStart(2, '0');
  const d = String(day).padStart(2, '0');
  return `${year}-${m}-${d}`;
}

export function DateRangeFilterEditor({
  fieldDef,
  filter,
  onApply,
  onClose,
}: DateRangeFilterEditorProps) {
  const { t, locale } = useI18n();
  const presets = fieldDef.datePresets || DEFAULT_DATE_PRESETS;

  // Check if current filter is a preset or specific dates
  const isInitialPreset = filter?.value?.startsWith('-') || filter?.value === 'today';
  const initialFrom = isInitialPreset ? '' : (filter?.value || '');
  const initialTo = filter?.highValue || '';

  const [startDate, setStartDate] = useState<string>(initialFrom);
  const [endDate, setEndDate] = useState<string>(initialTo);
  const [hoverDate, setHoverDate] = useState<string | null>(null);

  // Calendar month/year navigation state
  const now = new Date();
  const initialDateObj = initialFrom ? new Date(initialFrom) : now;
  const [currentYear, setCurrentYear] = useState<number>(
    isNaN(initialDateObj.getTime()) ? now.getFullYear() : initialDateObj.getFullYear()
  );
  const [currentMonth, setCurrentMonth] = useState<number>(
    isNaN(initialDateObj.getTime()) ? now.getMonth() : initialDateObj.getMonth()
  );

  const todayISO = formatDateISO(now.getFullYear(), now.getMonth(), now.getDate());

  const monthName = useMemo(() => {
    try {
      const formatted = new Intl.DateTimeFormat(locale, { month: 'long' }).format(
        new Date(currentYear, currentMonth, 1)
      );
      return formatted.charAt(0).toUpperCase() + formatted.slice(1);
    } catch {
      return `${currentMonth + 1}`;
    }
  }, [locale, currentYear, currentMonth]);

  const weekdays = useMemo(() => {
    try {
      // 2024-01-01 is a Monday
      return Array.from({ length: 7 }, (_, i) => {
        const formatted = new Intl.DateTimeFormat(locale, { weekday: 'short' }).format(
          new Date(2024, 0, 1 + i)
        );
        return formatted.replace(/\.$/, '').slice(0, 2);
      });
    } catch {
      return ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];
    }
  }, [locale]);

  // Generate days matrix for the displayed month (Monday start)
  const calendarDays = useMemo(() => {
    const firstDay = new Date(currentYear, currentMonth, 1);
    // Sunday is 0 in JS Date, convert so Monday is 0 and Sunday is 6
    let startingDayOfWeek = firstDay.getDay() - 1;
    if (startingDayOfWeek === -1) startingDayOfWeek = 6;

    const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
    const daysInPrevMonth = new Date(currentYear, currentMonth, 0).getDate();

    const days: { day: number; monthOffset: number; iso: string }[] = [];

    // Previous month padding days
    for (let i = startingDayOfWeek - 1; i >= 0; i--) {
      const d = daysInPrevMonth - i;
      const prevMonth = currentMonth === 0 ? 11 : currentMonth - 1;
      const prevYear = currentMonth === 0 ? currentYear - 1 : currentYear;
      days.push({ day: d, monthOffset: -1, iso: formatDateISO(prevYear, prevMonth, d) });
    }

    // Current month days
    for (let i = 1; i <= daysInMonth; i++) {
      days.push({ day: i, monthOffset: 0, iso: formatDateISO(currentYear, currentMonth, i) });
    }

    // Next month padding days to complete full weeks
    const remaining = 7 - (days.length % 7);
    if (remaining < 7) {
      for (let i = 1; i <= remaining; i++) {
        const nextMonth = currentMonth === 11 ? 0 : currentMonth + 1;
        const nextYear = currentMonth === 11 ? currentYear + 1 : currentYear;
        days.push({ day: i, monthOffset: 1, iso: formatDateISO(nextYear, nextMonth, i) });
      }
    }

    return days;
  }, [currentYear, currentMonth]);

  const handlePrevMonth = () => {
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear((y) => y - 1);
    } else {
      setCurrentMonth((m) => m - 1);
    }
  };

  const handleNextMonth = () => {
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear((y) => y + 1);
    } else {
      setCurrentMonth((m) => m + 1);
    }
  };

  const handlePresetClick = (preset: FilterPreset) => {
    onApply(preset.value);
    onClose();
  };

  const handleDayClick = (iso: string) => {
    if (!startDate || (startDate && endDate)) {
      setStartDate(iso);
      setEndDate('');
    } else if (startDate && !endDate) {
      if (iso < startDate) {
        setStartDate(iso);
        setEndDate(startDate);
      } else {
        setEndDate(iso);
      }
    }
  };

  const handleApplyCustom = () => {
    if (startDate) {
      onApply(startDate, endDate || undefined);
      onClose();
    }
  };

  const handleResetCalendar = () => {
    setStartDate('');
    setEndDate('');
    setHoverDate(null);
  };

  return (
    <div className="space-y-3.5">
      {/* Quick Presets Section */}
      <div>
        <div className="text-[0.68rem] font-semibold text-text-muted uppercase tracking-wider mb-1.5 flex items-center gap-1.5">
          <CalendarIcon className="w-3.5 h-3.5 text-accent" />
          <span>{t('filter.presets')}</span>
        </div>
        <div className="grid grid-cols-3 sm:grid-cols-5 gap-1.5">
          {presets.map((preset) => {
            const isSelected = filter?.value === preset.value && !filter?.highValue;
            const presetLabel = t(preset.label as any);
            return (
              <button
                key={preset.label}
                type="button"
                onClick={() => handlePresetClick(preset)}
                className={`py-1.5 px-2 rounded-md text-[0.75rem] font-medium transition-colors cursor-pointer text-center truncate ${
                  isSelected
                    ? 'bg-accent/20 text-accent border border-accent/40 font-semibold'
                    : 'bg-bg-surface border border-border-default text-text-secondary hover:text-text-primary hover:border-border-hover'
                }`}
                title={presetLabel}
              >
                {presetLabel}
              </button>
            );
          })}
        </div>
      </div>

      {/* Inline Calendar Section */}
      <div className="pt-2.5 border-t border-border-subtle">
        <div className="flex items-center justify-between mb-2">
          <div className="text-[0.68rem] font-semibold text-text-muted uppercase tracking-wider">
            {t('filter.customRange')}
          </div>
          {(startDate || endDate) && (
            <button
              type="button"
              onClick={handleResetCalendar}
              className="inline-flex items-center gap-1 text-[0.68rem] text-text-muted hover:text-text-primary transition-colors cursor-pointer"
            >
              <RotateCcw size={10} />
              <span>{t('filter.clear')}</span>
            </button>
          )}
        </div>

        {/* Calendar Box */}
        <div className="p-3 bg-bg-surface border border-border-default rounded-xl shadow-inner">
          {/* Month / Year Navigator */}
          <div className="flex items-center justify-between mb-2.5 px-1">
            <button
              type="button"
              onClick={handlePrevMonth}
              className="p-1 rounded-md text-text-muted hover:text-text-primary hover:bg-bg-hover transition-colors cursor-pointer"
              aria-label={t('filter.prevMonth' as any)}
            >
              <ChevronLeft size={16} />
            </button>
            <div className="text-xs font-semibold text-text-primary tracking-wide">
              {monthName} {currentYear}
            </div>
            <button
              type="button"
              onClick={handleNextMonth}
              className="p-1 rounded-md text-text-muted hover:text-text-primary hover:bg-bg-hover transition-colors cursor-pointer"
              aria-label={t('filter.nextMonth' as any)}
            >
              <ChevronRight size={16} />
            </button>
          </div>

          {/* Weekday Row */}
          <div className="grid grid-cols-7 gap-1 text-center mb-1">
            {weekdays.map((wd, idx) => (
              <div key={idx} className="text-[0.65rem] font-semibold text-text-muted">
                {wd}
              </div>
            ))}
          </div>

          {/* Days Grid */}
          <div className="grid grid-cols-7 gap-1 text-center">
            {calendarDays.map(({ day, monthOffset, iso }) => {
              const isCurrentMonth = monthOffset === 0;
              const isStart = iso === startDate;
              const isEnd = iso === endDate;
              const isToday = iso === todayISO;

              // Check if day is within selected or hovered range
              const effectiveEnd = endDate || (startDate && hoverDate && hoverDate > startDate ? hoverDate : null);
              const isInRange = startDate && effectiveEnd && iso > startDate && iso < effectiveEnd;

              let btnStyle = 'text-text-secondary hover:bg-bg-hover hover:text-text-primary';

              if (!isCurrentMonth) {
                btnStyle = 'text-text-muted/40 hover:text-text-muted hover:bg-bg-hover/50';
              }

              if (isStart || isEnd) {
                btnStyle = 'bg-accent text-white font-bold shadow-sm';
              } else if (isInRange) {
                btnStyle = 'bg-accent/20 text-accent font-medium';
              } else if (isToday) {
                btnStyle = 'text-text-primary font-semibold border border-accent/40';
              }

              return (
                <button
                  key={iso}
                  type="button"
                  onClick={() => handleDayClick(iso)}
                  onMouseEnter={() => startDate && !endDate && setHoverDate(iso)}
                  onMouseLeave={() => setHoverDate(null)}
                  className={`h-8 sm:h-7 rounded-md text-xs flex items-center justify-center transition-all cursor-pointer ${btnStyle}`}
                >
                  {day}
                </button>
              );
            })}
          </div>
        </div>

        {/* Selected Range Display */}
        <div className="flex items-center justify-between text-xs pt-2 text-text-secondary">
          <div className="truncate">
            {startDate ? (
              <span>
                <span className="text-text-muted font-mono">{t('filter.from')}:</span>{' '}
                <strong className="text-text-primary font-mono">{startDate}</strong>
                {endDate && (
                  <>
                    {' → '}
                    <span className="text-text-muted font-mono">{t('filter.to')}:</span>{' '}
                    <strong className="text-text-primary font-mono">{endDate}</strong>
                  </>
                )}
              </span>
            ) : (
              <span className="text-text-muted italic text-[0.72rem]">
                {t('filter.selectStartDate' as any)}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Footer Buttons */}
      <div className="flex justify-end gap-2 pt-2 border-t border-border-subtle">
        <button
          type="button"
          onClick={onClose}
          className="px-3 py-1.5 rounded-lg text-xs font-medium text-text-muted hover:text-text-primary hover:bg-bg-hover transition-colors cursor-pointer"
        >
          {t('filter.cancel')}
        </button>
        <button
          type="button"
          onClick={handleApplyCustom}
          disabled={!startDate}
          className={`px-4 py-1.5 rounded-lg text-xs font-medium transition-colors cursor-pointer shadow-sm ${
            startDate
              ? 'bg-accent text-white hover:bg-accent-hover'
              : 'bg-bg-surface text-text-muted border border-border-default opacity-50 cursor-not-allowed'
          }`}
        >
          {t('filter.apply')}
        </button>
      </div>
    </div>
  );
}
